'use client';
import { useId as useNewsletterId } from 'react';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { FiSend } from 'react-icons/fi';

export function Newsletter() {
  const seoNewsletterId = useNewsletterId();
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();
  const termsHref = t('text391');
  // This legacy label URL is 404; use the same verified terms page as the footer menu.
  const resolvedTermsHref =
    termsHref === '/vseobecne-obchodne-podmienky' ? '/obchodne-podmienky' : termsHref;

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const submitting = React.useRef(false);

  const subscribeMutation = useMutation({
    mutationFn: async (
      payload: Parameters<
        typeof import('@eliadmin/core/src/api/newsletter/newsletter-api').newsletterSubscribe
      >[0],
    ) => {
      const { newsletterSubscribe } =
        await import('@eliadmin/core/src/api/newsletter/newsletter-api');
      return newsletterSubscribe(payload);
    },
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore(s => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || submitting.current || subscribeMutation.isPending)
      return;
    submitting.current = true;
    subscribeMutation.mutate(
      {
        locale,
        body: {
          email,
          gdpr: checked,
          return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
        },
      },
      {
        onSettled: () => {
          submitting.current = false;
        },
      },
    );
  }

  return (
    <section id="newsletters" className={cn('p-2 sm:p-12', 'ea-homepage-newsletter')}>
      <form
        className={cn('grid grid-cols-12 gap-6 py-2', 'ea-homepage-newsletter__form')}
        onSubmit={handleSubmit}
      >
        <div
          className={cn(
            'relative z-[40] col-span-12 flex flex-col justify-center space-y-3 md:col-span-12',
            'ea-homepage-newsletter__form-item-email',
          )}
        >
          <div className="flex w-full items-center justify-between gap-2 sm:gap-3 lg:gap-6">
            <div className={cn('flex h-10 w-full flex-row items-center')}>
              <Input
                aria-label={systemLabels('email')}
                id={`newsletter-email-${seoNewsletterId}`}
                type="email"
                name="email"
                placeholder={t('text183')}
                onChange={e => setEmail(e.target.value)}
                className={{
                  wrapper: cn(
                    'border-none bg-input text-muted-foreground',
                    'ea-homepage-newsletter__form-item-email-input',
                  ),
                }}
              />
            </div>
            <Button
              type="submit"
              aria-label={t('text182')}
              variant={'dark'}
              className={cn('h-12 gap-0', 'ea-homepage-newsletter__form-item-email-submit-button')}
              disabled={email.length === 0 || !checked || isSubscribed}
              isLoading={subscribeMutation.isPending}
            >
              <p className="hidden sm:block">{t('text182')}</p> <FiSend size={20} />
            </Button>
          </div>

          {isSubscribed && <span className={cn('font-semibold text-success')}>{t('text234')}</span>}
          <div
            className={cn(
              'flex flex-row items-center space-x-2',
              'ea-homepage-newsletter__form-item-gdpr',
            )}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={value => setChecked(value === true)}
              id={`newsletter-gdpr-${seoNewsletterId}`}
              aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
              className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
              required
            />
            <Label
              htmlFor={`newsletter-gdpr-${seoNewsletterId}`}
              className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox-label')}
            >
              <span
                className={cn(
                  'text-foreground',
                  'ea-homepage-newsletter__form-item-gdpr-checkbox-span',
                )}
              >
                {t('text264')}&nbsp;
                <a
                  href={resolvedTermsHref ?? '#'}
                  className={cn(
                    'text-foreground underline',
                    'ea-homepage-newsletter__form-item-gdpr-checkbox-link1',
                  )}
                >
                  {t('text265')}
                </a>{' '}
                <a
                  href={t('text392') ?? '#'}
                  className={cn(
                    'text-foreground underline',
                    'ea-homepage-newsletter__form-item-gdpr-checkbox-link2',
                  )}
                >
                  {t('text436')}
                </a>
                .
              </span>
            </Label>
          </div>
        </div>
      </form>
    </section>
  );
}
